import { inject, Injectable } from '@angular/core';
import { Project } from '../classes/project';
import { IProjectHeading } from '../interfaces/project/project-heading.interface';
import { catchError, map, Observable, of, Subject, tap, throwError } from 'rxjs';
import { IDEA_API_SERVICE } from '@ideatech/ngx-ideatech';
import { ApiHolder } from '../classes/api-holder';
import { IProject } from '../interfaces/project/project.interface';
import { TranslocoService } from '@jsverse/transloco';
import { CommonModalUtils } from '../utils/common-modal-utils';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { IPostProjectRequest } from '../interfaces/api-requests/post-project-request.interface';
import { IPostProjectResponse } from '../interfaces/api-responses/post-project-response.interface';
import { IPutProjectRequest } from '../interfaces/api-requests/put-project-request.interface';
import { IWSDateTime } from '../interfaces/ws-datetime.interface';
import { WSDateTime } from '../classes/ws-datetime';
import { AlertsService } from './alerts.service';
import { IProjectSparePart } from '../interfaces/project/project-spare-part.interface';
import { DetailsLevel } from '../enums/details-level.enum';
import { IGetProjectReportPdfResponse } from '../interfaces/api-responses/get-project-report-pdf-response.interface';
import { buildDownloadFileName } from '../utils/file-name-utils';

@Injectable({
    providedIn: 'root'
})
export class ProjectService {

    private readonly apiService = inject(IDEA_API_SERVICE);
    private readonly translocoService = inject(TranslocoService);
    private readonly commonModalUtils = inject(CommonModalUtils);
    private readonly alertsService = inject(AlertsService);
    private readonly router = inject(Router);

    get project(): Project {
        if (!this._project) {
            this.showNotLoadError();
            throw new Error("No project load");
        }
        return this._project;
    }

    get projectTitleSafe(): string | undefined {
        return this._project?.title;
    }

    private _project?: Project;

    public create(requestData: IPostProjectRequest): Observable<number | null> {
        return this.apiService.request<IPostProjectResponse>(ApiHolder.project.post(requestData)).pipe(map(resultPostProject => resultPostProject.data?.idProject ?? null));
    }

    public get(id: number, details: DetailsLevel): Observable<Project> {
        return this.apiService.request<IProject>(ApiHolder.project.getById(id, details)).pipe(
            map(resultGetById => {
                this._project = Project.from(resultGetById.data!);
                return this._project;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.status === HttpStatusCode.NotFound) {
                    this.showNotFoundError();
                    return of();
                } else {
                    return throwError(() => error);
                }
            })
        );
    }

    public update(updateData: IPostProjectRequest, customerBusinessName: string): Observable<void> {
        const requestData: IPutProjectRequest = {
            idProject: this.project.idProject,
            ...updateData
        };
        return this.apiService.request<IProjectHeading>(ApiHolder.project.put(requestData)).pipe(
            map(resultPut => {
                if (resultPut.data!.idProject === this.project.idProject) {
                    this.updateHeading(resultPut.data!.title, resultPut.data!.idCustomer, customerBusinessName, resultPut.data!.updateDate, resultPut.data!.deliveryDate, resultPut.data!.note);
                    this.alertsService.open({
                        type: 'success',
                        delay: 5000,
                        dismissable: true,
                        message: this.translocoService.translate("IDS_PROJECT_UPDATE_SUCCESS_MESSAGE")
                    });
                } else {
                    this.showMismatchError();
                }
                return;
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.status === HttpStatusCode.NotFound) {
                    this.showNotFoundError();
                    return of();
                } else {
                    return throwError(() => error);
                }
            })
        );
    }

    public confirm(idProject: number): Observable<void> {
        return this.apiService.request(ApiHolder.project.confirm(idProject)).pipe(map(() => undefined));
    }

    public clone(idProject: number): Observable<number> {
        return this.apiService.request<IPostProjectResponse>(ApiHolder.project.clone({ idProject })).pipe(
            map(cloneResult => cloneResult.data!.idProject),
            catchError((error: HttpErrorResponse) => {
                if (error.status === HttpStatusCode.NotFound) {
                    this.showNotFoundError();
                    return of();
                } else {
                    return throwError(() => error);
                }
            })
        )
    }

    public addButtonPanel(idProjectButtonPanel: number, name: string, projectButtonPanelQuantity: number, projectUpdateDate: IWSDateTime): void {
        this.project.addButtonPanel(idProjectButtonPanel, name, projectButtonPanelQuantity);
        this.project.setUpdateDate(projectUpdateDate);
    }

    public updateButtonPanel(idProjectButtonPanel: number, name: string, quantity: number, projectUpdateDate: IWSDateTime): void {
        this.project.updateButtonPanel(idProjectButtonPanel, name, quantity)
        this.project.setUpdateDate(projectUpdateDate);
    }

    public deleteButtonPanel(idProjectButtonPanel: number, projectUpdateDate: IWSDateTime): void {
        this.project.deleteButtonPanel(idProjectButtonPanel);
        this.project.setUpdateDate(projectUpdateDate);
    }

    public setSpareParts(spareParts: IProjectSparePart[], projectUpdateDate: IWSDateTime): void {
        this.project.setSpareParts(spareParts);
        this.project.setUpdateDate(projectUpdateDate);
    }

    public updateSparePart(idProjectSparePart: number, quantity: number, projectUpdateDate: IWSDateTime): void {
        this.project.updateSparePart(idProjectSparePart, quantity);
        this.project.setUpdateDate(projectUpdateDate);
    }

    public deleteSparePart(idProjectSparePart: number, projectUpdateDate: IWSDateTime): void {
        this.project.deleteSparePart(idProjectSparePart);
        this.project.setUpdateDate(projectUpdateDate);
    }

    private updateHeading(title: string, idCustomer: number, customerBusinessName: string, updateDate: IWSDateTime, deliveryDate: IWSDateTime, note?: string): void {
        this.project.title = title;
        this.project.idCustomer = idCustomer;
        this.project.customerBusinessName = customerBusinessName;
        this.project.updateDate = WSDateTime.from(updateDate);
        this.project.deliveryDate = WSDateTime.from(deliveryDate);
        this.project.note = note;
    }

    private showNotLoadError(): void {
        const messageTranslation = this.translocoService.translate('IDS_NO_PROJECT_LOAD_ERROR');
        this.commonModalUtils.showErrorModal(messageTranslation)
            .then(() => this.router.navigate(["/", "projects"]))
            .catch(() => {});
    }

    private showNotFoundError(): void {
        const messageTranslation = this.translocoService.translate('IDS_PROJECT_NOT_FOUND_ERROR');
        this.commonModalUtils.showErrorModal(messageTranslation)
            .then(() => this.router.navigate(["/", "projects"]))
            .catch(() => {});
    }

    private showMismatchError(): void {
        const messageTranslation = this.translocoService.translate('IDS_PROJECT_MISMATCH_ERROR');
        this.commonModalUtils.showErrorModal(messageTranslation)
            .then(() => this.router.navigate(["/", "projects"]))
            .catch(() => {});
    }

    public downloadPdfReport(idProject: number, withPrices: boolean): void {

        const formData = new FormData();
        formData.append('idProject', idProject.toString());
        formData.append('language', this.translocoService.getActiveLang());
        formData.append('withPrices', withPrices.toString());
        this.apiService.request<IGetProjectReportPdfResponse>(ApiHolder.report.GenerateProjectReportPdf(formData))
                .subscribe(response => {
                if (response.data) {
                    if (response.data.pdfBytes) {
                        const byteCharacters = atob(response.data.pdfBytes);
                        const byteNumbers = new Uint8Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const blob = new Blob([byteNumbers], { type: 'application/pdf' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = buildDownloadFileName(this._project?.title, 'pdf', 'report');
                        a.click();
                        URL.revokeObjectURL(url);
                    }
                    else {
                        const message = this.translocoService.translate("IDS_REPORD_ERROR") + "[ " + response.data.message +  " ]";
                        this.alertsService.open({
                            type: 'danger',
                            delay: 5000,
                            dismissable: true,
                            message: message
                        });
                    }
                }
            });
    }
}
